import React, { useMemo, useState } from 'react';
import './CultureSettingsComponent.scss';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { LocaleLanguageCode, SupportedLanguages } from 'Constants/Common';
import {
    getTranslatedCountryAndLanguage,
    normalizeLanguageCode
} from 'helpers/location';
import { ValuesType } from 'types/generic';

const getLanguageLabel = (code: string) => {
    const labels: Record<
        ValuesType<typeof LocaleLanguageCode>,
        { language: string; country?: string }
    > = {
        [LocaleLanguageCode.ES]: { language: 'Español', country: 'España' }
    };

    return labels[code] ?? getTranslatedCountryAndLanguage(code);
};

export const CultureSettingsComponent = () => {
    const { i18n, t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const options = useMemo(() => {
        const available = SupportedLanguages.map((e) =>
            normalizeLanguageCode(e)
        );

        const unique = Array.from(new Set(available));

        return unique
            .map((code) => {
                const { language: label, country } = getLanguageLabel(code);

                return {
                    code,
                    label,
                    country
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, []);

    const currentLanguage = normalizeLanguageCode(i18n.language);

    const changeLanguage = (code: string) => {
        i18n.changeLanguage(code);
        setIsOpen(false);
    };

    if (options.length <= 1) return null;

    return (
        <>
            <button
                type='button'
                className='button-icon btn-culture'
                onClick={() => setIsOpen((v) => !v)}>
                <FontAwesomeIcon size='xl' icon={faGlobe} />
            </button>

            <Modal
                isOpen={isOpen}
                toggle={() => setIsOpen(false)}
                centered
                scrollable
                size='md'>
                <ModalHeader toggle={() => setIsOpen(false)}>
                    {t('choose_language')}
                </ModalHeader>

                <ModalBody className='language-list'>
                    {options.map((o) => (
                        <button
                            key={o.code}
                            className={`language-item ${o.code === currentLanguage ? 'active' : ''}`}
                            onClick={() => changeLanguage(o.code)}>
                            <div>{o.label}</div>
                            <div className='small text-secondary'>
                                {o.country ? ` ${o.country}` : ''}
                            </div>
                        </button>
                    ))}
                </ModalBody>
            </Modal>
        </>
    );
};
